import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CognitoUserPool } from 'amazon-cognito-identity-js';
import config from '../auth/config';
import './ApplicationReview.css';

const ROWS_PER_PAGE = 5;
const FETCH_LIMIT = 100;

const userPool = new CognitoUserPool({
  UserPoolId: config.userPoolId,
  ClientId: config.userPoolClientId,
});

const getAccessToken = () => new Promise((resolve, reject) => {
  const currentUser = userPool.getCurrentUser();
  if (!currentUser) {
    reject(new Error('No user logged in'));
    return;
  }

  currentUser.getSession((err, session) => {
    if (err) {
      reject(err);
      return;
    }

    resolve(session.getAccessToken().getJwtToken());
  });
});

const STATUS_DISPLAY = {
  queued: { label: 'Queued', className: 'status-pending' },
  running: { label: 'Running', className: 'status-study' },
  succeeded: { label: 'Succeeded', className: 'status-success' },
  failed: { label: 'Failed', className: 'status-failed' },
};

const STATUS_RANK = {
  queued: 0,
  running: 1,
  failed: 2,
  succeeded: 3,
};

const sortDefaults = {
  projectName: 'asc',
  submittedOn: 'desc',
  status: 'desc',
};

const SEARCHABLE_KEYS = ['jobId', 'projectName', 'feeder', 'status'];

const getSortText = (sortKey, direction) => {
  if (sortKey === 'projectName') return direction === 'asc' ? 'A to Z' : 'Z to A';
  if (sortKey === 'submittedOn') return direction === 'desc' ? 'Most Recent' : 'Least Recent';
  if (sortKey === 'status') return direction === 'desc' ? 'Most Ahead' : 'Least Ahead';
  return '';
};

const compareProjectName = (left, right) => {
  const leftValue = String(left ?? '').trim().toLowerCase();
  const rightValue = String(right ?? '').trim().toLowerCase();
  if (!leftValue && rightValue) return 1;
  if (leftValue && !rightValue) return -1;
  return leftValue.localeCompare(rightValue);
};

const formatCapacities = (capacities) => {
  if (!Array.isArray(capacities) || capacities.length === 0) {
    return '-';
  }

  const values = capacities.map((value) => Number(value)).filter((value) => Number.isFinite(value));
  if (values.length === 0) {
    return '-';
  }

  return `${values.at(-1).toLocaleString('en-US')} kW`;
};

const formatSubmitted = (iso) => {
  if (!iso) return '-';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const compareStatus = (left, right) => (STATUS_RANK[left] ?? -1) - (STATUS_RANK[right] ?? -1);

function ApplicationReview() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'submittedOn', direction: 'desc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchJobs = useCallback(async ({ silent } = {}) => {
    if (!silent) {
      setIsRefreshing(true);
    }

    try {
      const accessToken = await getAccessToken();
      const response = await fetch(`/api/ramp-exposure?limit=${FETCH_LIMIT}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to load applications (status ${response.status})`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('API returned non-JSON response. Check that your dev proxy is configured.');
      }

      const body = await response.json();
      setItems(Array.isArray(body.items) ? body.items : []);
      setLastUpdatedAt(new Date());
      setFetchError('');
    } catch (error) {
      setFetchError(error.message || 'Failed to load applications');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs({ silent: true });
  }, [fetchJobs]);

  const queueCount = useMemo(
    () => items.filter((item) => item.status === 'queued' || item.status === 'running').length,
    [items],
  );
  const approvedCount = useMemo(
    () => items.filter((item) => item.status === 'succeeded').length,
    [items],
  );

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) =>
      SEARCHABLE_KEYS.some((key) => String(item[key] ?? '').toLowerCase().includes(query)),
    );
  }, [items, searchQuery]);

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const sortDirection = direction === 'asc' ? 1 : -1;

    sorted.sort((left, right) => {
      let result = 0;

      if (key === 'projectName') {
        result = compareProjectName(left.projectName, right.projectName);
      } else if (key === 'submittedOn') {
        result = Date.parse(left.createdAt || '') - Date.parse(right.createdAt || '');
      } else if (key === 'status') {
        result = compareStatus(left.status, right.status);
      }

      return result * sortDirection;
    });

    return sorted;
  }, [filteredItems, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / ROWS_PER_PAGE));
  const clampedPage = Math.min(currentPage, totalPages);
  const paginatedItems = sortedItems.slice(
    (clampedPage - 1) * ROWS_PER_PAGE,
    clampedPage * ROWS_PER_PAGE,
  );

  const selectedItem = useMemo(
    () => (selectedJobId ? items.find((item) => item.jobId === selectedJobId) ?? null : null),
    [items, selectedJobId],
  );

  const updateSort = (sortKey) => {
    setSortConfig((previousSort) => {
      if (previousSort.key === sortKey) {
        return {
          key: sortKey,
          direction: previousSort.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        key: sortKey,
        direction: sortDefaults[sortKey],
      };
    });
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const SortHeader = ({ label, sortKey, className = '' }) => {
    const isActive = sortConfig.key === sortKey;
    const direction = isActive ? sortConfig.direction : sortDefaults[sortKey];
    const directionSymbol = direction === 'asc' ? '↑' : '↓';

    return (
      <th className={className}>
        <div className="header-with-sort">
          <span>{label}</span>
          <button
            className={`sort-button ${isActive ? 'sort-active' : ''}`}
            onClick={() => updateSort(sortKey)}
            aria-label={`Sort ${label} (${getSortText(sortKey, direction)})`}
            title={`Sort: ${getSortText(sortKey, direction)}`}
            type="button"
          >
            {directionSymbol}
          </button>
        </div>
      </th>
    );
  };

  const renderTableBody = () => {
    if (isLoading && items.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="empty-row">Loading applications...</td>
        </tr>
      );
    }

    if (!isLoading && fetchError && items.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="empty-row">
            {fetchError}
          </td>
        </tr>
      );
    }

    if (paginatedItems.length === 0) {
      return (
        <tr>
          <td colSpan={7} className="empty-row">No applications match your search.</td>
        </tr>
      );
    }

    return paginatedItems.map((item) => {
      const statusInfo = STATUS_DISPLAY[item.status] ?? { label: item.status || 'Unknown', className: 'status-pending' };

      return (
        <tr
          key={item.jobId}
          className={selectedJobId === item.jobId ? 'row-selected' : ''}
          onClick={() => setSelectedJobId((prev) => (prev === item.jobId ? null : item.jobId))}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setSelectedJobId((prev) => (prev === item.jobId ? null : item.jobId));
            }
          }}
        >
          <td className="col-app-id col-job-id">{item.jobId}</td>
          <td className="col-project">{item.projectName || '-'}</td>
          <td>{item.feeder || '-'}</td>
          <td>{formatCapacities(item.capacities)}</td>
          <td className="col-submitted">{formatSubmitted(item.createdAt)}</td>
          <td className="col-status">
            <span className={`status-chip ${statusInfo.className}`}>
              {statusInfo.label}
            </span>
          </td>
          <td
            className="col-report"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {item.downloadUrl ? (
              <a
                href={item.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="report-link"
              >
                Download
              </a>
            ) : (
              <span className="report-empty">-</span>
            )}
          </td>
        </tr>
      );
    });
  };

  const renderDetailPanel = () => {
    if (!selectedItem) return null;

    const statusInfo = STATUS_DISPLAY[selectedItem.status] ?? { label: selectedItem.status || 'Unknown', className: 'status-pending' };
    const rawProgress = Number(selectedItem.progressPct ?? 0);
    const progressPct = Number.isFinite(rawProgress) ? Math.max(0, Math.min(100, rawProgress)) : 0;
    const isFailed = selectedItem.status === 'failed';
    const phaseLabel = isFailed ? 'failed' : (selectedItem.phase || 'queued');

    return (
      <article className="application-detail-card">
        <div className="application-detail-header">
          <h3>{selectedItem.jobId}</h3>
          <span className={`status-chip ${statusInfo.className}`}>{statusInfo.label}</span>
        </div>

        <div className="application-progress" aria-label="Job progress">
          <div className="progress-bar-header">
            <span className="detail-label">Progress</span>
            <span className="progress-bar-pct">{progressPct}%</span>
          </div>
          <div
            className={`progress-bar ${isFailed ? 'is-failed' : ''}`}
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="progress-bar-fill" style={{ width: `${progressPct}%` }} />
          </div>
          <span className="progress-bar-phase">{phaseLabel}</span>
        </div>

        <div className="application-detail-grid">
          <div>
            <span className="detail-label">Feeder</span>
            <p>{selectedItem.feeder || '-'}</p>
          </div>
          <div>
            <span className="detail-label">Capacity</span>
            <p>{formatCapacities(selectedItem.capacities)}</p>
          </div>
          <div>
            <span className="detail-label">Simulation Window</span>
            <p>{(selectedItem.start || '-') + ' to ' + (selectedItem.end || '-')}</p>
          </div>
          <div>
            <span className="detail-label">Submitted</span>
            <p>{formatSubmitted(selectedItem.createdAt)}</p>
          </div>
          <div>
            <span className="detail-label">Last Update</span>
            <p>{formatSubmitted(selectedItem.updatedAt)}</p>
          </div>
        </div>

        {selectedItem.errorMessage && (
          <div className="application-detail-note">
            <span className="detail-label">Error</span>
            <p>{selectedItem.errorMessage}</p>
          </div>
        )}

        {selectedItem.downloadUrl && (
          <a
            className="ramp-primary-button detail-download-button"
            href={selectedItem.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Report
          </a>
        )}
      </article>
    );
  };

  return (
    <section className="review-page">
      <div className="review-header">
        <div>
          <h2>Application Review</h2>
        </div>
      </div>

      <div className="review-metrics">
        <article className="metric-card">
          <span className="metric-label">Applications in Progress</span>
          <strong>{queueCount}</strong>
        </article>
        <article className="metric-card">
          <span className="metric-label">Succeeded</span>
          <strong>{approvedCount}</strong>
        </article>
      </div>

      {fetchError && items.length > 0 && (
        <div className="refresh-error" role="status">
          Failed to refresh; showing last known state ({fetchError}).
        </div>
      )}

      <div className="table-toolbar">
        <div className="search-wrapper">
          <span className="search-icon" aria-hidden="true">⌕</span>
          <input
            type="text"
            className="search-input"
            placeholder="Search"
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        <div className="toolbar-actions">
          {lastUpdatedAt && (
            <span className="last-updated">
              Updated {lastUpdatedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            className="refresh-button"
            onClick={() => fetchJobs()}
            disabled={isRefreshing}
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="table-wrap">
        <table className="applications-table">
          <thead>
            <tr>
              <th className="col-app-id col-job-id">Job ID</th>
              <SortHeader label="Project" sortKey="projectName" className="col-project" />
              <th>Feeder</th>
              <th>Capacity</th>
              <SortHeader label="Submitted" sortKey="submittedOn" className="col-submitted" />
              <SortHeader label="Status" sortKey="status" className="col-status" />
              <th className="col-report">Report</th>
            </tr>
          </thead>
          <tbody>
            {renderTableBody()}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            disabled={clampedPage <= 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            type="button"
          >
            ← Prev
          </button>
          <span className="pagination-info">
            Page {clampedPage} of {totalPages}
          </span>
          <button
            className="pagination-button"
            disabled={clampedPage >= totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            type="button"
          >
            Next →
          </button>
        </div>
      )}

      {renderDetailPanel()}
    </section>
  );
}

export default ApplicationReview;
